import { Component } from '@theme/component';

/**
 * Wraps a slideshow-component inside a mega menu to handle:
 * - Centering slides when content fits without overflow
 * - Showing/hiding arrows based on actual scroll overflow
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} slideshow - The inner slideshow-component
 *
 * @extends {Component<Refs>}
 */
class MegaMenuCarousel extends Component {
  /** @type {MutationObserver | null} */
  #initObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {ResizeObserver | null} */
  #visibilityObserver = null;

  /** @type {number} */
  #gap = 0;

  /** @type {boolean} */
  #swiperConfigured = false;

  // /** @type {string} */
  // #debugId = '';

  // /** @type {number} */
  // #updateCount = 0;

  connectedCallback() {
    super.connectedCallback();
    // // Identify this carousel by its type class for easier log reading
    // const slideshowEl = this.querySelector('.mega-menu__carousel');
    // const typeClass = slideshowEl
    //   ? [...slideshowEl.classList].find((c) => c.startsWith('mega-menu__carousel--'))?.replace('mega-menu__carousel--', '') || 'unknown'
    //   : 'unknown';
    // this.#debugId = `[MegaMenu:${typeClass}]`;
    // console.log(`${this.#debugId} connectedCallback — offsetWidth: ${this.offsetWidth}, offsetHeight: ${this.offsetHeight}, visible: ${this.offsetWidth > 0}`);
    this.#waitForSwiper();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // console.log(`${this.#debugId} disconnectedCallback — was configured: ${this.#swiperConfigured}, total updates: ${this.#updateCount}`);
    this.#initObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#visibilityObserver?.disconnect();
  }

  /**
   * Wait for the inner slideshow-component's Swiper to initialize.
   * Uses both MutationObserver (catches DOM changes from Swiper init)
   * and ResizeObserver (catches visibility transitions from content-visibility)
   * to reliably detect when Swiper is ready, regardless of timing.
   */
  #waitForSwiper() {
    const slideshow = this.querySelector('slideshow-component');
    if (!slideshow) return;

    const scroller = slideshow.querySelector('slideshow-slides');
    if (!scroller) return;

    if (scroller.swiper) {
      // console.log(`${this.#debugId} Swiper already initialized on first check`);
      this.#onSwiperReady(scroller.swiper, slideshow);
      return;
    }

    // console.log(`${this.#debugId} Swiper not ready yet, setting up observers to wait...`);

    const checkAndInit = () => {
      if (this.#swiperConfigured) return;

      if (scroller.swiper) {
        this.#swiperConfigured = true;
        this.#initObserver?.disconnect();
        this.#initObserver = null;
        this.#visibilityObserver?.disconnect();
        this.#visibilityObserver = null;
        // console.log(`${this.#debugId} Swiper detected via observer — initializing`);
        this.#onSwiperReady(scroller.swiper, slideshow);
      }
    };

    // Watch for Swiper init via DOM changes (Swiper adds classes/styles on init)
    this.#initObserver = new MutationObserver(checkAndInit);
    this.#initObserver.observe(scroller, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    // Watch for visibility transitions — when the parent submenu changes
    // from content-visibility: auto to visible, this element gains width.
    // Swiper may initialize shortly after, so poll briefly with rAF.
    this.#visibilityObserver = new ResizeObserver(() => {
      // const entry = entries[0];
      // const w = entry?.contentRect?.width || 0;
      // const h = entry?.contentRect?.height || 0;

      if (this.#swiperConfigured || this.offsetWidth === 0) {
        // if (w > 0) {
        //   console.log(`${this.#debugId} ResizeObserver fired but skipped — configured: ${this.#swiperConfigured}, offsetWidth: ${this.offsetWidth}, contentRect: ${w}x${h}`);
        // }
        return;
      }

      // console.log(`${this.#debugId} ResizeObserver — element became visible, contentRect: ${w}x${h}, checking for Swiper...`);

      // Element became visible — Swiper may need a frame to initialize
      // after slideshow.js detects overflow and creates the instance
      checkAndInit();

      if (!this.#swiperConfigured) {
        requestAnimationFrame(() => {
          requestAnimationFrame(checkAndInit);
        });
      }
    });
    this.#visibilityObserver.observe(this);
  }

  /**
   * @param {object} swiper
   * @param {HTMLElement} slideshow
   */
  #onSwiperReady(swiper, slideshow) {
    // console.log(`${this.#debugId} onSwiperReady — slides: ${swiper.slides?.length}, slidesPerView: ${swiper.params.slidesPerView}, containerWidth: ${swiper.width}, wrapperWidth: ${swiper.wrapperEl?.scrollWidth}`);

    // Force auto slide widths — Swiper may have initialized inside a hidden
    // container (e.g., closed accordion in the drawer) and incorrectly
    // detected single-slide mode, setting inline widths on each slide.
    if (swiper.params.slidesPerView !== 'auto') {
      // console.warn(`${this.#debugId} ⚠ slidesPerView was "${swiper.params.slidesPerView}", forcing to "auto" — Swiper likely initialized while hidden`);
      swiper.params.slidesPerView = 'auto';

      for (const slide of swiper.slides) {
        slide.style.removeProperty('width');
      }
    }

    // Enable observer so Swiper recalculates when parent visibility changes
    // (e.g. content-visibility: auto → visible on submenu activation)
    swiper.params.observer = true;
    swiper.params.observeParents = true;
    swiper.params.observeSlideChildren = true;

    // Read gap from CSS variable and configure Swiper's spaceBetween
    // so it accounts for gaps in translate/snap calculations
    const gapValue = getComputedStyle(this).getPropertyValue('--slideshow-gap');
    this.#gap = parseInt(gapValue) || 0;

    if (this.#gap > 0) {
      swiper.params.spaceBetween = this.#gap;
      // console.log(`${this.#debugId} spaceBetween set to ${this.#gap}px from --slideshow-gap`);
    }

    // Re-initialize observers and recalculate dimensions
    swiper.update();

    // // Log Swiper state after update
    // console.log(`${this.#debugId} Post-update state — translate: ${swiper.translate}, maxTranslate: ${swiper.maxTranslate()}, snapGrid: [${swiper.snapGrid?.join(', ')}], isBeginning: ${swiper.isBeginning}, isEnd: ${swiper.isEnd}`);

    // // Log individual slide dimensions for layout debugging
    // if (swiper.slides?.length) {
    //   const slideWidths = Array.from(swiper.slides).map((s, i) => `slide${i}: ${s.offsetWidth}px`);
    //   console.log(`${this.#debugId} Slide widths: ${slideWidths.join(', ')}`);
    // }

    let lastWidth = this.offsetWidth;

    const update = () => this.#updateState(swiper, slideshow);

    const resizeUpdate = () => {
      const currentWidth = this.offsetWidth;

      // When transitioning from hidden (0) to visible, recalculate Swiper
      if (lastWidth === 0 && currentWidth > 0) {
        // console.log(`${this.#debugId} Visibility transition 0→${currentWidth}px — triggering swiper.update()`);
        swiper.update();
      }

      lastWidth = currentWidth;
      update();
    };

    update();

    swiper.on('reachBeginning', update);
    swiper.on('reachEnd', update);
    swiper.on('fromEdge', update);
    swiper.on('slideChange', update);
    swiper.on('resize', update);
    swiper.on('snapGridLengthChange', update);
    swiper.on('setTranslate', update);
    swiper.on('transitionEnd', update);

    // // Log Swiper events with details for loop/jitter/transition debugging
    // swiper.on('reachBeginning', () => {
    //   console.log(`${this.#debugId} EVENT reachBeginning — translate: ${swiper.translate}`);
    //   update();
    // });
    // swiper.on('reachEnd', () => {
    //   console.log(`${this.#debugId} EVENT reachEnd — translate: ${swiper.translate}, maxTranslate: ${swiper.maxTranslate()}`);
    //   update();
    // });
    // swiper.on('fromEdge', () => {
    //   console.log(`${this.#debugId} EVENT fromEdge — translate: ${swiper.translate}`);
    //   update();
    // });
    // swiper.on('slideChange', () => {
    //   console.log(`${this.#debugId} EVENT slideChange — activeIndex: ${swiper.activeIndex}, realIndex: ${swiper.realIndex}, translate: ${swiper.translate}`);
    //   update();
    // });
    // swiper.on('resize', () => {
    //   console.log(`${this.#debugId} EVENT resize — containerWidth: ${swiper.width}, wrapperScrollWidth: ${swiper.wrapperEl?.scrollWidth}`);
    //   update();
    // });
    // swiper.on('snapGridLengthChange', () => {
    //   console.log(`${this.#debugId} EVENT snapGridLengthChange — new grid: [${swiper.snapGrid?.join(', ')}], slidesGrid: [${swiper.slidesGrid?.join(', ')}]`);
    //   update();
    // });
    // swiper.on('setTranslate', (_, translate) => {
    //   this.#updateCount++;
    //   // Only log every 10th setTranslate to avoid flooding (this fires on every frame during drag)
    //   if (this.#updateCount % 10 === 0) {
    //     console.log(`${this.#debugId} EVENT setTranslate (${this.#updateCount}) — translate: ${translate}, progress: ${swiper.progress?.toFixed(3)}`);
    //   }
    //   update();
    // });
    // swiper.on('transitionStart', () => {
    //   console.log(`${this.#debugId} EVENT transitionStart — from index ${swiper.previousIndex} to ${swiper.activeIndex}, translate: ${swiper.translate}, speed: ${swiper.params.speed}ms`);
    // });
    // swiper.on('transitionEnd', () => {
    //   console.log(`${this.#debugId} EVENT transitionEnd — activeIndex: ${swiper.activeIndex}, translate: ${swiper.translate}, isBeginning: ${swiper.isBeginning}, isEnd: ${swiper.isEnd}`);
    //   update();
    // });

    // // Additional loop-specific debugging
    // if (swiper.params.loop) {
    //   console.warn(`${this.#debugId} ⚠ Loop mode is ENABLED — this can cause jitter/duplicate slides`);
    //   swiper.on('loopFix', () => {
    //     console.log(`${this.#debugId} EVENT loopFix — realIndex: ${swiper.realIndex}, activeIndex: ${swiper.activeIndex}`);
    //   });
    // }

    // // Log if Swiper has any unusual params that could cause glitches
    // const params = swiper.params;
    // console.log(`${this.#debugId} Swiper config — loop: ${params.loop}, cssMode: ${params.cssMode}, freeMode: ${params.freeMode?.enabled ?? params.freeMode}, centeredSlides: ${params.centeredSlides}, speed: ${params.speed}, resistance: ${params.resistance}, resistanceRatio: ${params.resistanceRatio}`);

    this.#resizeObserver = new ResizeObserver(resizeUpdate);
    this.#resizeObserver.observe(this);
  }

  /**
   * @param {object} swiper
   * @param {HTMLElement} slideshow
   */
  #updateState(swiper, slideshow) {
    const wrapperEl = swiper.wrapperEl;
    const containerWidth = swiper.width || this.offsetWidth;

    // Compute overflow from intrinsic slide widths + the configured gap.
    // Reading scrollWidth here would include the current marginRight/gap
    // we ourselves apply, which creates a feedback loop: opening the menu
    // alternates between centered and left-aligned because each pass sees
    // the previous pass's styles and flips its decision.
    let totalSlidesWidth = 0;
    for (const slide of swiper.slides) {
      totalSlidesWidth += slide.offsetWidth;
    }
    const totalGap = Math.max(0, swiper.slides.length - 1) * this.#gap;
    const hasOverflow = totalSlidesWidth + totalGap > containerWidth;

    this.toggleAttribute('data-no-overflow', !hasOverflow);

    // // Log when overflow state changes (key for layout debugging)
    // const prevNoOverflow = this.hasAttribute('data-no-overflow');
    // if (prevNoOverflow !== !hasOverflow) {
    //   console.log(`${this.#debugId} Overflow state CHANGED — hasOverflow: ${hasOverflow}, containerWidth: ${containerWidth}, scrollWidth: ${scrollWidth}, diff: ${scrollWidth - containerWidth}px`);
    // }

    // When overflowing, Swiper handles spacing via spaceBetween/margin-right
    // so remove CSS gap to avoid doubling. When not overflowing, let CSS gap
    // handle spacing and disable Swiper's margin-right.
    if (wrapperEl) {
      wrapperEl.style.gap = hasOverflow ? '0' : '';
    }
    for (const slide of swiper.slides) {
      slide.style.marginRight = hasOverflow ? `${this.#gap}px` : '0px';
    }

    const prev = slideshow.querySelector('.slideshow-control--previous');
    const next = slideshow.querySelector('.slideshow-control--next');

    if (prev) prev.disabled = !hasOverflow || swiper.translate >= 0;
    if (next) {
      // Max translate is negative when there's overflow content
      const maxTranslate = swiper.maxTranslate();
      next.disabled = !hasOverflow || swiper.translate <= maxTranslate;
    }

    // // Log arrow state for debugging navigation issues
    // if (prev && next) {
    //   const prevChanged = prev.disabled !== prevDisabled;
    //   const nextChanged = next.disabled !== nextDisabled;
    //   if (prevChanged || nextChanged) {
    //     console.log(`${this.#debugId} Arrow state — prev: ${prevDisabled ? 'disabled' : 'enabled'}, next: ${nextDisabled ? 'disabled' : 'enabled'}, translate: ${swiper.translate.toFixed(1)}, maxTranslate: ${maxTranslate.toFixed(1)}`);
    //   }
    // }

    // // Detect potential jitter: translate bouncing near edges
    // if (Math.abs(swiper.translate) < 1 && swiper.translate !== 0) {
    //   console.warn(`${this.#debugId} ⚠ Potential jitter — translate is near-zero: ${swiper.translate}`);
    // }
    // if (Math.abs(swiper.translate - maxTranslate) < 1 && swiper.translate !== maxTranslate) {
    //   console.warn(`${this.#debugId} ⚠ Potential jitter — translate near maxTranslate: ${swiper.translate} vs ${maxTranslate}`);
    // }

    // // Detect sizing mismatch (container reports 0 width while having content)
    // if (containerWidth === 0 && swiper.slides?.length > 0) {
    //   console.warn(`${this.#debugId} ⚠ Container width is 0 but has ${swiper.slides.length} slides — likely hidden/collapsed`);
    // }
  }
}

if (!customElements.get('mega-menu-carousel')) {
  customElements.define('mega-menu-carousel', MegaMenuCarousel);
}
